import React, { useContext, useState } from 'react';
import DataTable from '@preflighttech/preflight-tables';
import { Text, View, Pressable } from 'react-native';
import { SALES_WITH_COUNT, HIDE_SALE } from './queries';
import { useMutation } from '@apollo/client';
import SubNav from './SubNav';
import { Link } from 'app/src/utils/routing';
import Filter from 'app/src/elements/Filter';
import { filterButton } from 'app/src/elements/Pagination/FilterStyles';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import {
   MainContainer,
   ContentContainer,
   TableContainer,
   PageTitle,
   LinkContainer
} from '../../elements';
import moment from 'moment';
import { STATUSES } from './elements';
import Modal from '../../elements/Modal';
import EditSale from './edit';
import AuthContext from 'app/src/contexts/AuthContext';
import { Button } from 'app/src/elements/buttons';

const inputStyle = {
  marginBottom: '10px'
}

const Sales = () => {
  const [refetch, setRefetch] = useState();
  const [clickedSale, setClickedSale] = useState();
  const [showEditSaleModal, setShowEditSaleModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const { hasPolicy } = useContext(AuthContext);
  const [currentScope, setCurrentScope] = useState('open')

  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [currentScope],
  };
  const [hideSale] = useMutation(
    HIDE_SALE, { onCompleted: refetch }
  )

  const onHide = (saleId) => {
    hideSale({ variables: { id: saleId }});
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;

    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const columns = [
    {
      key: 'name',
      label: 'Sale',
      content: (entry) => {
        return (
          <View>
            <Link to={`/sales/${entry.entry.id}`}>
              {entry.entry.name}
            </Link>
          </View>
        )
      }
    },
    {
      key: 'saleType',
      label: 'Type',
    },
    {
      key: 'saleOn',
      label: 'Sale Date',
      sort: 'desc',
      content: (entry) => {
        return moment(entry.entry.saleOn)?.format('MM-DD-YYYY')
      }
    },
    {
      key: 'startAt',
      label: 'Start',
      content: (entry) => {
        return moment(entry.entry.startAt)?.format('MM-DD-YY hh:mmA')
      }
    },
    {
      key: 'endAt',
      label: 'End',
      content: (entry) => {
        return moment(entry.entry.endAt)?.format('MM-DD-YY hh:mmA')
      }
    },
    {
      key: 'preBidOpenAt',
      label: 'PreBid',
      content: (entry) => {
        return moment(entry.entry.preBidOpenAt)?.format('MM-DD-YY hh:mmA')
      }
    },
    {
      key: 'recurringSaleId',
      label: 'Recurring?',
      content: (entry) => {
        return entry.entry.recurringSale?.name
      }
    },
    {
      key: 'status',
      label: 'Status',
      content: (entry) => {
        return entry.entry.status
      }
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      hidden: hasPolicy({ type: 'Sale', action: 'bidding_only' }),
      content: ({ entry }) => {
        return (
          <LinkContainer>
            {hasPolicy({ type: 'Sale', action: 'update' }) && (
              <>
                <Pressable
                  onPress={() => {
                    setClickedSale(entry);
                    setShowEditSaleModal(true)
                  }}
                  key={`sale-edit-link-${entry.id}`}
                    name={`sale-edit-link-${entry.id}`}
                >
                  <Text>
                    Edit
                  </Text>
                </Pressable>
                <Text>{' | '}</Text>
              </>
            )}
            <Link to={`/sales/${entry.id}/pre`}>
              Pre-sale List
            </Link>
            <Text>{' | '}</Text>
            <Link to={`/sales/${entry.id}/live`}>
              Live Sale
            </Link>
            <Text>{' | '}</Text>
            <Link to={`/sales/${entry.id}/post`}>
              Post-sale List
            </Link>
          </LinkContainer>
        )
      }
    },
    {
      key: 'paddle-functions',
      label: 'Paddle Service',
      sort: 'prevent',
      hidden: (
        !hasPolicy({ type: 'Sale', action: 'auctioneer' }) &&
        !hasPolicy({ type: 'Sale', action: 'clerk' })
      ),
      content: ({ entry }) => (
        <LinkContainer>
          {hasPolicy({ type: 'Sale', action: 'auctioneer' }) && (
            <>
              <Link to={`/bidding/auctioneer/${entry.id}`}>
                Auctioneer
              </Link>
              <Text>{' | '}</Text>
            </>
          )}
          {hasPolicy({ type: 'Sale', action: 'clerk' }) && (
            <>
              <Link to={`/bidding/clerk/${entry.id}`}>
                Clerk
              </Link>
              <Text>{' | '}</Text>
            </>
          )}
          {hasPolicy({ type: 'Sale', action: 'auctioneer' }) && (
            <>
              <Link to={`/bidding/hud/${entry.id}`}>
                HUD
              </Link>
              <Text>{' | '}</Text>
            </>
          )}
          <Link to={`/bidding/streams/${entry.id}`}>
            Recordings
          </Link>
          <Text>{' | '}</Text>
          <Link to={`/bidding/events/${entry.id}`}>
            History
          </Link>
        </LinkContainer>
      )
    },
  ]

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      {'processing_and_closed' === currentScope ? (
        <Button
          style={filterButton}
          label={`Show Open`}
          onPress={() => setCurrentScope('open')}
        />
      ) : (
        <Button
          style={filterButton}
          label={`Show Processing/Closed`}
          onPress={() => setCurrentScope('processing_and_closed')}
        />
      )}
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px', justifyContent: 'space-between'}}>
          <Text>Sale Name:</Text>
          <input
            type={'text'}
            name="name"
            onChange={e => setFilter('name', e.target.value)}
            value={filters.name || ''}
            style={inputStyle}
          />
          {/* <Text>Sale Date:</Text>
          <input
            type={'text'}
            name="saleOn"
            onChange={e => setFilter('saleOn', e.target.value)}
            value={filters.saleOn || ''}
            style={inputStyle}
          /> */}
          <Text>Start:</Text>
          <input
            type={'date'}
            name="startAt"
            onChange={e => setFilter('startAt', e.target.value)}
            value={filters.startAt || ''}
            style={inputStyle}
          />
          <Text>End:</Text>
          <input
            type={'date'}
            name="endAt"
            onChange={e => setFilter('endAt', e.target.value)}
            value={filters.endAt || ''}
            style={inputStyle}
          />
          <Text>Status:</Text>
          <select
            type={'select'}
            name="status"
            onChange={e => setFilter('status', e.target.value)}
            value={filters.status || ''}
          >
            <option key={`status-all`}>
              {'All'}
            </option>
            {STATUSES.map(status =>
              <option key={`status-${status}`}>
                {status}
              </option>
            )}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>Sales</PageTitle>
        <br /><br />
        <TableContainer>
          <SubNav
            refetch={refetch}
            tableNav={true}
          />
          <DataTable.Gql
            setRefetch={setRefetch}
            query={SALES_WITH_COUNT}
            queryName='salesWithCount'
            columns={columns}
            buttons={buttons}
            lengthMenu={ [10, 25, 50, 'All'] }
            setSettings={setSettings}
            dataUpdated={data => setFilterOptions(data?.salesWithCount)}
            htmlTable
            queryVariables={queryVariables}
          />
        </TableContainer>
      </ContentContainer>
      {showEditSaleModal &&
        <Modal
          refetch={refetch}
          showModal={showEditSaleModal}
          setShowModal={setShowEditSaleModal}
          modalType={'Edit Sale'}
          objectName={clickedSale.name}
        >
          <EditSale
            setShowModal={setShowEditSaleModal}
            refetch={refetch}
            sale={clickedSale}
            statusUpdate={false}
          />
        </Modal>
      }
    </MainContainer>
  )
}

export default Sales;
